import React, {useEffect} from 'react';
import {useFormikContext} from 'formik';
import {useTranslation} from 'react-i18next';
import {Box, Grid} from '@material-ui/core';
import CustomizedCheckbox from '../../Checkbox/Checkbox';
import IconWithTooltip from '../../Tooltip/IconWithTooltip';
import classNames from 'classnames';
import FileSelect from '../../FileSelect/FileSelect';
import {PromptFormProps, PromptsFormType, promptsFormValidationSchema, useStyles,} from './PromptForm.utils';
import {VoiceFileContentType} from '../../../store/types/Voicemail';
import {prepareOggFile} from '../../UploadFile/UploadFile';

const PromptForm: React.FC<PromptFormProps> = ({handleSetIsValidFunc, callback}) => {
    const classes = useStyles();
    const {t} = useTranslation();

    const {values, setFieldValue, errors, setFieldError, isValid} = useFormikContext<PromptsFormType>();

    useEffect(() => {

        const validationKeys = Object.keys(promptsFormValidationSchema.fields);
        const errorsKeys = Object.keys(errors);

        const hasErrors = validationKeys.some(key => errorsKeys.includes(key));

        if (hasErrors) {
            callback && callback();
            handleSetIsValidFunc?.('prompts', () => false);

        } else {
            handleSetIsValidFunc?.('prompts', () => true);
        }

    }, [errors, values])

    const inputFileNameResolver = {
        introPromptStatus: 'introPromptFileName',
        menuPromptStatus:'menuPromptFileName',
        onTimeoutPromptStatus:'onTimeoutPromptFileName',
        onUnavailableSelectionPromptStatus:'onUnavailableSelectionPromptFileName'
    };


    const handleCheckBoxChange = (field: string, e: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue(field, e.target.checked);
        // @ts-ignore
        setFieldError(inputFileNameResolver[field], undefined);
    }

    return (
        <div className={classes.inputs}>
            <Grid item className={classes.itemsContainer}>
                <Grid
                    className={classNames(
                        classes.centeredGrid,
                        classes.playBeforeGrid,
                    )}
                >
                    <CustomizedCheckbox
                        checked={values.introPromptStatus}
                        dataQa="intro-status"
                        onChange={(value) =>
                            handleCheckBoxChange('introPromptStatus', value)
                        }
                        label={t('screens:autoAttendants.intro')}
                    />

                    <IconWithTooltip
                        dataQa="intro-tooltip"
                        tooltipText={t('tooltips:autoAttendants.intro')}
                    />
                </Grid>

                {values.introPromptStatus && (
                    <Box
                        className={classes.file}
                        alignItems="center"
                        display="flex"
                    >
                        <FileSelect
                            nameValue={values.introPromptFileName}
                            onFileChange={(file) => {
                                let audioFile;
                                if (
                                    file.type === VoiceFileContentType.VideoOgg
                                ) {
                                    audioFile = prepareOggFile(file);
                                }

                                setFieldValue(
                                    'introPromptFileName',
                                    audioFile ? audioFile.name : file.name,
                                );
                                setFieldValue(
                                    'introPromptFile',
                                    audioFile ?? file,
                                );

                                errors.introPromptFileName?.length && setFieldError('introPromptFileName', '')
                            }}
                            file={values.introPromptFile}
                            customStyles={{
                                textField: classes.buttonFileTextField,
                                buttonStyle: classes.buttonFile,
                            }}
                            helperText={errors.introPromptFileName}
                        />
                    </Box>
                )}

                <Grid
                    className={classNames(
                        classes.centeredGrid,
                        classes.playBeforeGrid,
                    )}
                >
                    <CustomizedCheckbox
                        checked={values.menuPromptStatus}
                        dataQa="menu-status"
                        onChange={(value) =>
                            handleCheckBoxChange('menuPromptStatus', value)
                        }
                        label={t('screens:autoAttendants.menu')}
                    />

                    <IconWithTooltip
                        dataQa="menu-tooltip"
                        tooltipText={t('tooltips:autoAttendants.menu')}
                    />
                </Grid>

                {values.menuPromptStatus && (
                    <Box
                        className={classes.file}
                        alignItems="center"
                        display="flex"
                    >
                        <FileSelect
                            nameValue={values.menuPromptFileName}
                            onFileChange={(file) => {
                                let audioFile;
                                if (
                                    file.type === VoiceFileContentType.VideoOgg
                                ) {
                                    audioFile = prepareOggFile(file);
                                }

                                setFieldValue(
                                    'menuPromptFileName',
                                    audioFile ? audioFile.name : file.name,
                                );
                                setFieldValue(
                                    'menuPromptFile',
                                    audioFile ?? file,
                                );

                                errors.menuPromptFileName?.length && setFieldError('menuPromptFileName', '')

                            }}
                            file={values.menuPromptFile}
                            customStyles={{
                                textField: classes.buttonFileTextField,
                                buttonStyle: classes.buttonFile,
                            }}
                            helperText={errors.menuPromptFileName}
                        />
                    </Box>
                )}

                <Grid
                    className={classNames(
                        classes.centeredGrid,
                        classes.playBeforeGrid,
                    )}
                >
                    <CustomizedCheckbox
                        checked={values.onTimeoutPromptStatus}
                        dataQa="on-timeout-status"
                        onChange={(value) =>
                            handleCheckBoxChange('onTimeoutPromptStatus', value)
                        }
                        label={t('screens:autoAttendants.onTimeout')}
                    />

                    <IconWithTooltip
                        dataQa="on-timeout-tooltip"
                        tooltipText={t('tooltips:autoAttendants.onTimeout')}
                    />
                </Grid>

                {values.onTimeoutPromptStatus && (
                    <Box
                        className={classes.file}
                        alignItems="center"
                        display="flex"
                    >
                        <FileSelect
                            nameValue={values.onTimeoutPromptFileName}
                            onFileChange={(file) => {
                                let audioFile;
                                if (
                                    file.type === VoiceFileContentType.VideoOgg
                                ) {
                                    audioFile = prepareOggFile(file);
                                }

                                setFieldValue(
                                    'onTimeoutPromptFileName',
                                    audioFile ? audioFile.name : file.name,
                                );
                                setFieldValue(
                                    'onTimeoutPromptFile',
                                    audioFile ?? file,
                                );

                                errors.onTimeoutPromptFileName?.length && setFieldError('onTimeoutPromptFileName', '')

                            }}
                            file={values.onTimeoutPromptFile}
                            customStyles={{
                                textField: classes.buttonFileTextField,
                                buttonStyle: classes.buttonFile,
                            }}
                            helperText={errors.onTimeoutPromptFileName}
                        />
                    </Box>
                )}

                <Grid
                    className={classNames(
                        classes.centeredGrid,
                        classes.playBeforeGrid,
                    )}
                >
                    <CustomizedCheckbox
                        checked={values.onUnavailableSelectionPromptStatus}
                        dataQa="on-unavailable-status"
                        onChange={(value) =>
                            handleCheckBoxChange('onUnavailableSelectionPromptStatus', value)

                        }
                        label={t(
                            'screens:autoAttendants.onUnavailableSelection',
                        )}
                    />

                    <IconWithTooltip
                        dataQa="on-unavailable-tooltip"
                        tooltipText={t('tooltips:autoAttendants.onUnavailable')}
                    />
                </Grid>

                {values.onUnavailableSelectionPromptStatus && (
                    <Box
                        className={classes.file}
                        alignItems="center"
                        display="flex"
                    >
                        <FileSelect
                            nameValue={
                                values.onUnavailableSelectionPromptFileName
                            }
                            onFileChange={(file) => {
                                let audioFile;
                                if (
                                    file.type === VoiceFileContentType.VideoOgg
                                ) {
                                    audioFile = prepareOggFile(file);
                                }

                                setFieldValue(
                                    'onUnavailableSelectionPromptFileName',
                                    audioFile ? audioFile.name : file.name,
                                );
                                setFieldValue(
                                    'onUnavailableSelectionPromptFile',
                                    audioFile ?? file,
                                );

                                errors.onUnavailableSelectionPromptFile?.length && setFieldError('onUnavailableSelectionPromptFile', '')

                            }}
                            file={values.onUnavailableSelectionPromptFile}
                            customStyles={{
                                textField: classes.buttonFileTextField,
                                buttonStyle: classes.buttonFile,
                            }}
                            helperText={errors.onUnavailableSelectionPromptFileName}
                        />
                    </Box>
                )}
            </Grid>
        </div>
    );
};

export default PromptForm;
